import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, CalendarDays, ChevronDown, ChevronRight, CircleAlert,
  CheckCircle2, Filter, Info, Loader2, Plus, RefreshCw, ShieldAlert, Users, XCircle,
} from 'lucide-react';
import { apiFetch } from '@/lib/supabaseClient';
import { Button } from '@/components/ui/button';
import { PeriodEditor } from '@/pages/FerieAnalisi';
import { toast } from 'sonner';

const formatNumber = (value) => Number(value || 0).toLocaleString('it-IT', { maximumFractionDigits: 2 });
const dateFormatter = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long' });
const objectiveDateFormatter = new Intl.DateTimeFormat('it-IT', { day: 'numeric', month: 'long', year: 'numeric' });
const monthFormatter = new Intl.DateTimeFormat('it-IT', { month: 'long' });
const monthOptions = Array.from({ length: 12 }, (_, month) => ({
  value: month,
  label: monthFormatter.format(new Date(2024, month, 1)),
}));

function formatDate(value) {
  if (!value) return '';
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : dateFormatter.format(parsed);
}

function formatObjectiveDate(value, fallback = 'oltre la data obiettivo') {
  if (!value) return fallback;
  const parsed = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? value : objectiveDateFormatter.format(parsed);
}

function isoDate(year, month, day) {
  return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

function monthWithData(groups, year) {
  const dates = groups
    .flatMap((group) => group.dettagliCalcolo?.capacitaGiornaliera || [])
    .map((item) => item.data)
    .filter((value) => value?.startsWith(`${year}-`));
  if (!dates.length) return 0;
  return Math.min(...dates.map((value) => Number(value.slice(5, 7)) - 1));
}

function formatLocationLabel(group) {
  const labels = group.gruppoCondiviso
    ? (group.localiGruppo || []).join(' + ') || group.localeNome
    : group.localeNome;
  return (labels || 'Altro locale').split(' — Gruppo', 1)[0].trim();
}

function objectiveStatus(status) {
  if (status === 'COPERTURA_OK' || status === 'COPERTURA_POSSIBILE') {
    return { className: 'text-emerald-700', label: status === 'COPERTURA_POSSIBILE' ? 'COPERTURA POSSIBILE' : 'COPERTURA OK' };
  }
  if (status === 'FERIE_DA_RIPORTARE') return { className: 'text-orange-800', label: 'FERIE DA RIPORTARE' };
  if (status === 'SERVONO_CUOCHI') return { className: 'text-orange-800', label: 'SERVONO CUOCHI' };
  return { className: 'text-red-800', label: 'DA VERIFICARE' };
}

function statusFor(group) {
  const target = formatObjectiveDate(group.dettagliCalcolo?.dataFineProiezione, 'la data obiettivo');
  if (group.stato === 'FERIE_PIANIFICABILI') {
    return {
      label: 'COPERTURA OK',
      sentence: `Tutti possono finire le ferie entro ${target}.`,
      className: 'border-emerald-200 bg-emerald-50 text-emerald-700',
      iconClassName: 'bg-emerald-500',
    };
  }
  if (group.stato === 'FABBISOGNO_AGGIUNTIVO') {
    const amount = Number(group.fabbisognoAggiuntivo || 0);
    return {
      label: `SERVONO ${amount} ${amount === 1 ? 'CUOCO' : 'CUOCHI'} IN PIÙ`,
      sentence: `Per permettere a tutti di finire le ferie entro ${target}.`,
      className: 'border-orange-200 bg-orange-50 text-orange-800',
      iconClassName: 'bg-orange-500',
    };
  }
  return {
    label: 'Da verificare',
    sentence: 'Mancano informazioni sufficienti per confermare il piano.',
    className: 'border-red-200 bg-red-50 text-red-800',
    iconClassName: 'bg-red-500',
  };
}

function StatusLine({ group }) {
  const status = statusFor(group);
  return (
    <div className={`rounded-xl border px-3 py-2.5 ${status.className}`}>
      <div className="flex items-center gap-2 text-sm font-bold">
        <span className={`h-2.5 w-2.5 rounded-full ${status.iconClassName}`} aria-hidden="true" />
        <span>{status.label}</span>
      </div>
      <p className="mt-1 text-xs leading-5 opacity-90">{status.sentence}</p>
    </div>
  );
}

function PeriodSummary({ periods }) {
  if (!periods?.length) return null;
  return (
    <div className="flex items-start gap-2 text-xs text-orange-900">
      <CalendarDays className="mt-0.5 h-4 w-4 shrink-0 text-orange-600" />
      <div className="space-y-1">
        {periods.map((period) => (
          <div key={`${period.dal}-${period.al}-${period.fabbisognoAggiuntivo}`}>
            <strong>Periodo critico:</strong> {formatDate(period.dal)} → {formatDate(period.al)}
            {period.fabbisognoAggiuntivo ? ` · servono ${period.fabbisognoAggiuntivo} ${period.fabbisognoAggiuntivo === 1 ? 'cuoco' : 'cuochi'} in più` : ''}
          </div>
        ))}
      </div>
    </div>
  );
}

function CoverageSummary({ group }) {
  const needed = Number(group.fabbisognoAggiuntivo || 0);
  if (!needed) return null;
  const sources = group.coperturaPerLocale || [];
  return (
    <div className="rounded-xl border border-blue-100 bg-blue-50/60 p-3 text-xs text-blue-950">
      <div className="flex items-start gap-2">
        <Users className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" />
        <div className="min-w-0 flex-1">
          <strong>Possibile copertura interna</strong>
          <p className="mt-1 text-blue-900/75">È una proposta di verifica: non modifica ferie, turni o assegnazioni.</p>
          {sources.length ? (
            <div className="mt-1 space-y-1">
              {sources.map((source) => (
                <div key={source.localeNome} className="flex justify-between gap-3">
                  <span>{source.localeNome}</span>
                  <span className="font-semibold">+{source.disponibili}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-1">Nessuna copertura disponibile negli altri locali nel periodo necessario.</p>
          )}
          <div className="mt-2 border-t border-blue-200 pt-2 font-semibold">
            Totale copertura disponibile: +{formatNumber(group.coperturaDisponibile)}
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, tone = 'navy', testId }) {
  const colors = { navy: 'text-brand-navy', amber: 'text-orange-700', green: 'text-brand-green', stone: 'text-stone-700', info: 'text-blue-700' };
  return (
    <div className="rounded-xl bg-stone-50 px-3 py-2.5" data-testid={testId}>
      <div className={`font-display text-lg font-semibold ${colors[tone]}`}>{value}</div>
      <div className="mt-0.5 text-[10px] font-bold uppercase tracking-wider text-stone-500">{label}</div>
    </div>
  );
}

function PlanningOverview({ data, year, groups, onOpenCalendar }) {
  const objective = data?.obiettivi?.find((item) => item.anno === year) || {};
  const kpi = data?.kpi || {};
  const criticalGroups = groups.filter((group) => group.periodiCritici?.length > 0);
  const criticalPeriods = criticalGroups.reduce((total, group) => total + (group.periodiCritici?.length || 0), 0);
  const coverageAvailable = groups.reduce((total, group) => total + Number(group.coperturaDisponibile || 0), 0);
  const coverageGroups = criticalGroups.filter((group) => Number(group.coperturaDisponibile || 0) > 0);
  const locationLabels = criticalGroups.map(formatLocationLabel);

  return (
    <section className="rounded-2xl border border-brand-blue/20 bg-gradient-to-br from-blue-50/80 via-app-surface to-app-surface p-4 sm:p-5" aria-label="Riepilogo pianificatore" data-testid="ferie-pianificazione-overview">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-brand-blue" aria-hidden="true" />
            <h2 className="font-display text-lg font-semibold text-brand-navy">Pianificatore ferie</h2>
          </div>
          <p className="mt-1 max-w-2xl text-xs leading-5 text-stone-600">
            Il riepilogo usa i risultati del motore ferie. La capacità annuale resta distinta dal controllo giornaliero della copertura.
          </p>
        </div>
        <button type="button" onClick={onOpenCalendar} className="inline-flex items-center gap-1.5 rounded-full border border-brand-blue/30 bg-white px-3 py-2 text-xs font-semibold text-brand-blue hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40" data-testid="ferie-pianificazione-open-calendar">
          <CalendarDays className="h-4 w-4" aria-hidden="true" /> Apri calendario
        </button>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Metric label="Ferie da gestire" value={`${formatNumber(objective.ferieTotaliDaGestire ?? kpi.daPianificare)} gg`} tone="navy" testId="ferie-pianificazione-kpi-da-gestire" />
        <Metric label="Capacità con organico attuale" value={`${formatNumber(objective.ferieUtilizzabiliConOrganico ?? kpi.ferieUtilizzabiliConOrganico)} gg`} tone="green" testId="ferie-pianificazione-kpi-capacita" />
        <Metric label="Ferie residue" value={`${formatNumber(objective.ferieResidueFineAnno ?? kpi.ferieNonUtilizzabili)} gg`} tone="amber" testId="ferie-pianificazione-kpi-residue" />
        <Metric label="Margine organico" value={`${Number(kpi.margine || 0) >= 0 ? '+' : ''}${formatNumber(kpi.margine)}`} tone="green" testId="ferie-pianificazione-kpi-margine" />
      </div>

      <div className="mt-2 grid grid-cols-2 gap-2 lg:grid-cols-4">
        <Metric label="Cuochi aggiuntivi necessari" value={`+${formatNumber(objective.cuochiAggiuntiviNecessari ?? kpi.fabbisognoAggiuntivo)}`} tone="amber" testId="ferie-pianificazione-kpi-cuochi" />
        <Metric label="Periodi critici" value={formatNumber(criticalPeriods)} tone={criticalPeriods ? 'amber' : 'green'} testId="ferie-pianificazione-kpi-periodi-critici" />
        <Metric label="Copertura interna proposta" value={coverageAvailable ? `+${formatNumber(coverageAvailable)}` : 'Nessuna'} tone={coverageAvailable ? 'info' : 'amber'} testId="ferie-pianificazione-kpi-copertura" />
        <Metric label="Capacità annuale teorica" value={`${formatNumber(objective.capacitaFerieTeorica ?? kpi.capacitaFerieTeorica)} gg`} tone="info" testId="ferie-pianificazione-kpi-capacita-annuale" />
      </div>

      <div className="mt-3 grid gap-2 lg:grid-cols-2">
        <div className="rounded-xl border border-orange-100 bg-orange-50/70 p-3 text-xs text-orange-950" data-testid="ferie-pianificazione-critical-groups">
          <div className="flex items-center gap-2 font-semibold"><ShieldAlert className="h-4 w-4 text-orange-600" aria-hidden="true" /> Locali e gruppi interessati</div>
          {locationLabels.length ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {locationLabels.map((label) => <span key={label} className="rounded-full border border-orange-200 bg-white/70 px-2 py-1">{label}</span>)}
            </div>
          ) : <p className="mt-1 text-orange-900/75">Nessun locale o gruppo presenta criticità nel periodo selezionato.</p>}
        </div>
        <div className="rounded-xl border border-blue-100 bg-blue-50/70 p-3 text-xs text-blue-950" data-testid="ferie-pianificazione-coverage-summary">
          <div className="flex items-center gap-2 font-semibold">
            {coverageAvailable ? <Users className="h-4 w-4 text-brand-blue" aria-hidden="true" /> : <CircleAlert className="h-4 w-4 text-orange-600" aria-hidden="true" />}
            Possibilità di copertura interna
          </div>
          {coverageGroups.length ? (
            <p className="mt-1 text-blue-900/80">Il motore ha individuato una proposta in {coverageGroups.length} {coverageGroups.length === 1 ? 'locale/gruppo' : 'locali/gruppi'}. Apri “Trova copertura” nel dettaglio interessato.</p>
          ) : (
            <p className="mt-1 text-blue-900/80">Nessuna proposta sicura disponibile per i periodi critici. Non vengono modificati automaticamente ferie o turni.</p>
          )}
        </div>
      </div>
    </section>
  );
}

function CalendarView({ groups, year }) {
  const dailyRows = useMemo(
    () => groups.flatMap((group) => (group.dettagliCalcolo?.capacitaGiornaliera || []).map((day) => ({
      ...day,
      groupKey: `${group.gruppoId || group.localeId}-${group.repartoId || 'none'}`,
      groupLabel: `${formatLocationLabel(group)} · ${group.repartoNome}`,
    }))),
    [groups],
  );
  const dayMap = useMemo(() => {
    const mapped = new Map();
    dailyRows.forEach((row) => {
      const current = mapped.get(row.data) || { date: row.data, rows: [], critical: false };
      current.rows.push(row);
      current.critical = current.critical || Number(row.extraNecessario || 0) > 0 || Number(row.presentiDopoFerie || 0) < Number(row.minimo || 0);
      mapped.set(row.data, current);
    });
    return mapped;
  }, [dailyRows]);
  const firstMonth = monthWithData(groups, year);
  const [month, setMonth] = useState(firstMonth);
  const [selectedDate, setSelectedDate] = useState(null);

  useEffect(() => {
    setMonth((current) => {
      const hasMonth = dailyRows.some((item) => Number(item.data.slice(5, 7)) - 1 === current);
      return hasMonth ? current : firstMonth;
    });
  }, [dailyRows, firstMonth]);

  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const firstWeekday = (new Date(year, month, 1).getDay() + 6) % 7;
  const monthDates = Array.from({ length: daysInMonth }, (_, index) => isoDate(year, month, index + 1));
  const selected = selectedDate ? dayMap.get(selectedDate) : null;
  const monthRows = monthDates.map((date) => dayMap.get(date)).filter(Boolean);

  useEffect(() => {
    const firstCritical = monthRows.find((item) => item.critical);
    const nextDate = firstCritical?.date || monthRows[0]?.date || null;
    setSelectedDate((current) => (current && dayMap.has(current) && current.slice(0, 7) === `${year}-${String(month + 1).padStart(2, '0')}` ? current : nextDate));
  }, [year, month, dailyRows.length, dayMap, monthRows]);

  return (
    <section id="ferie-pianificazione-calendario" className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5" aria-label="Calendario di copertura" data-testid="ferie-pianificazione-calendar">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-brand-blue" aria-hidden="true" />
            <h2 className="font-display text-lg font-semibold text-brand-navy">Calendario di copertura</h2>
          </div>
          <p className="mt-1 text-xs leading-5 text-stone-500">Controllo giorno per giorno della simulazione ferie. Non sostituisce la capacità annuale e non modifica il piano.</p>
        </div>
        <label className="grid gap-1 text-xs font-semibold text-stone-600">
          Mese
          <select aria-label="Mese calendario ferie" value={month} onChange={(event) => setMonth(Number(event.target.value))} className="h-9 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm font-normal capitalize text-brand-navy">
            {monthOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </label>
      </div>

      {!dailyRows.length ? (
        <div className="mt-4 rounded-xl border border-dashed border-stone-300 bg-stone-50 p-6 text-center text-sm text-stone-500">Nessun dato giornaliero disponibile per il periodo selezionato.</div>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[10px] font-bold uppercase tracking-wide text-stone-400">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((label) => <span key={label}>{label}</span>)}
          </div>
          <div className="mt-1 grid grid-cols-7 gap-1" role="grid" aria-label={`Calendario ${monthOptions[month].label} ${year}`}>
            {Array.from({ length: firstWeekday }, (_, index) => <span key={`empty-${index}`} className="min-h-11 rounded-lg bg-stone-50/50 sm:min-h-14" aria-hidden="true" />)}
            {monthDates.map((date) => {
              const day = dayMap.get(date);
              const dayNumber = Number(date.slice(8, 10));
              const isSelected = selectedDate === date;
              return (
                <button
                  key={date}
                  type="button"
                  disabled={!day}
                  onClick={() => day && setSelectedDate(date)}
                  className={`min-h-11 rounded-lg border p-1 text-left transition sm:min-h-14 ${!day ? 'border-transparent bg-stone-50/50 text-stone-300' : day.critical ? 'border-orange-200 bg-orange-50 text-orange-950 hover:border-orange-400' : 'border-emerald-100 bg-emerald-50/60 text-emerald-950 hover:border-emerald-300'} ${isSelected ? 'ring-2 ring-brand-blue ring-offset-1' : ''}`}
                  data-testid={`ferie-pianificazione-calendar-day-${date}`}
                  aria-label={`${formatDate(date)}${day?.critical ? ', giornata critica' : day ? ', copertura ok' : ', nessun dato'}`}
                >
                  <span className="text-xs font-semibold">{dayNumber}</span>
                  {day && <span className="mt-1 block truncate text-[9px] font-semibold">{day.critical ? 'Critica' : 'OK'}</span>}
                </button>
              );
            })}
          </div>
          <div className="mt-3 flex flex-wrap gap-3 text-[11px] text-stone-600" aria-label="Legenda calendario">
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-orange-400" /> Criticità</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" /> Copertura ok</span>
            <span className="inline-flex items-center gap-1"><span className="h-2.5 w-2.5 rounded-full bg-stone-300" /> Nessun dato nel motore</span>
          </div>
          {selected && (
            <div className="mt-4 rounded-xl border border-stone-100 bg-stone-50/70 p-3" data-testid="ferie-pianificazione-calendar-details">
              <div className="flex items-center gap-2 text-sm font-semibold text-brand-navy">
                {selected.critical ? <ShieldAlert className="h-4 w-4 text-orange-600" aria-hidden="true" /> : <CheckCircle2 className="h-4 w-4 text-emerald-600" aria-hidden="true" />}
                {formatObjectiveDate(selected.date)}
              </div>
              <div className="mt-2 space-y-2">
                {selected.rows.map((row) => (
                  <div key={`${row.groupKey}-${row.data}`} className="rounded-lg border border-white bg-white p-2.5 text-xs">
                    <div className="font-semibold text-brand-navy">{row.groupLabel}</div>
                    <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-1 text-stone-600 sm:grid-cols-4">
                      <span>Ferie simulate: <strong>{formatNumber(row.ferieSimulate)} gg</strong></span>
                      <span>Presenti: <strong>{formatNumber(row.presentiDopoFerie)}</strong></span>
                      <span>Minimo: <strong>{formatNumber(row.minimo)}</strong></span>
                      <span>Assenze: <strong>{formatNumber(row.assenzeFisse)}</strong></span>
                    </div>
                    <div className={`mt-1 font-semibold ${Number(row.extraNecessario || 0) > 0 ? 'text-orange-700' : 'text-emerald-700'}`}>
                      {Number(row.extraNecessario || 0) > 0 ? `Criticità: servono +${formatNumber(row.extraNecessario)} cuochi` : 'Copertura ok con l’organico del giorno'}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function GeneralSummary({ kpi, summary, objectives }) {
  if (!kpi || !summary) return null;
  return (
    <section className="rounded-2xl border border-app-border bg-app-surface p-4 sm:p-5" aria-label="Riepilogo generale" data-testid="ferie-pianificazione-general-summary">
      <div className="flex flex-wrap items-end justify-between gap-2">
        <div>
          <h2 className="font-display text-lg font-semibold text-brand-navy">COPERTURA FERIE</h2>
          <p className="mt-1 text-xs text-stone-500">Tutte le cucine nel perimetro autorizzato.</p>
        </div>
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-4">
        <Metric label="Cuochi in organico" value={formatNumber(summary.cuochiOrganico)} tone="navy" />
        <Metric label="Cuochi minimi necessari" value={formatNumber(summary.cuochiMinimiNecessari)} tone="amber" />
        <Metric label="Margine attuale" value={`+${formatNumber(summary.margineAttuale)}`} tone="green" />
        <Metric label="Ferie residue totali" value={`${formatNumber(summary.ferieResidue)} gg`} tone="amber" />
      </div>
      <div className="mt-4 grid gap-2 lg:grid-cols-2">
        {(objectives || []).map((objective) => {
          const state = objectiveStatus(objective.stato);
          return (
            <div key={objective.dataObiettivo} className="rounded-xl border border-stone-100 bg-stone-50/70 p-3" data-testid={`ferie-pianificazione-objective-${objective.anno}`}>
              <div className="text-xs font-bold uppercase tracking-wider text-brand-navy">Entro {formatObjectiveDate(objective.dataObiettivo)}</div>
              <div className="mt-2 text-xs text-stone-600">Con l’organico attuale</div>
               <div className="mt-1 flex items-baseline justify-between gap-3">
                 <span className="text-xs text-stone-500">Capacità ferie teorica del margine</span>
                 <strong className="text-sm text-brand-navy">{formatNumber(objective.capacitaFerieTeorica)} gg</strong>
               </div>
               <div className="mt-1 flex items-baseline justify-between gap-3">
                 <span className="text-xs text-stone-500">Capacità reale del calendario</span>
                 <strong className="text-sm text-brand-navy">{formatNumber(objective.capacitaFerieCalendario)} gg</strong>
               </div>
               <div className="mt-1 flex items-baseline justify-between gap-3">
                 <span className="text-xs text-stone-500">Ferie effettivamente smaltibili</span>
                 <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieUtilizzabiliConOrganico)} gg</strong>
               </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie riportate dall’anno precedente</span>
                <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieRiportateDaAnnoPrecedente)} gg</strong>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie maturate nell’anno</span>
                <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieMaturateNellAnno)} gg</strong>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie utilizzate nell’anno</span>
                <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieUtilizzateNellAnno)} gg</strong>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie totali da gestire</span>
                <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieTotaliDaGestire)} gg</strong>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie residue a fine anno</span>
                <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieResidueFineAnno)} gg</strong>
              </div>
               <div className="mt-1 flex items-baseline justify-between gap-3">
                 <span className="text-xs text-stone-500">Ferie residue teoriche</span>
                 <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieResidueTeoriche)} gg</strong>
               </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie completabili entro</span>
                <strong className="text-sm text-brand-navy">{formatObjectiveDate(objective.ferieCompletateEntro)}</strong>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Ferie riportate al prossimo anno</span>
                <strong className="text-sm text-brand-navy">{formatNumber(objective.ferieRiportateAlProssimoAnno)} gg</strong>
              </div>
              <div className="mt-1 flex items-baseline justify-between gap-3">
                <span className="text-xs text-stone-500">Cuochi aggiuntivi necessari</span>
                <strong className="text-sm text-brand-navy">+{formatNumber(objective.cuochiAggiuntiviNecessari)}</strong>
              </div>
              <div className={`mt-2 text-xs font-bold ${state.className}`}>{state.label}</div>
              {objective.cuochiAggiuntiviNecessari > 0 && (
                <p className="mt-1 text-xs leading-5 text-stone-600">
                  Per completare tutte le ferie entro il {formatObjectiveDate(objective.dataObiettivo)} servono altri {formatNumber(objective.cuochiAggiuntiviNecessari)} cuochi.
                </p>
              )}
              {objective.scenariOrganico?.length > 0 && (
                <div className="mt-3 rounded-xl border border-blue-100 bg-blue-50/50 p-3" data-testid={`ferie-pianificazione-scenarios-${objective.anno}`}>
                  <div className="text-xs font-semibold text-blue-950">Simulazione organico</div>
                  <div className="mt-2 space-y-1.5">
                    {objective.scenariOrganico.map((scenario) => (
                      <div key={scenario.cuochiAggiuntivi} className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-0.5 text-xs text-blue-950">
                        <span>
                          <strong>+{scenario.cuochiAggiuntivi} cuochi</strong> · {formatNumber(scenario.organico)} in organico
                        </span>
                        <span className={scenario.calendarioCompleto ? 'font-semibold text-emerald-700' : 'text-stone-600'}>
                          {scenario.calendarioCompleto
                            ? 'Tutte le ferie completate'
                            : `${formatNumber(scenario.ferieSmaltibili)} gg smaltibili · ${formatNumber(scenario.ferieResidue)} gg residue`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {objective.ferieRiportateAlProssimoAnno > 0 && (
                <p className="mt-1 text-xs leading-5 text-stone-600">
                  Con l’organico attuale non è possibile smaltire tutte le ferie entro il {formatObjectiveDate(objective.dataObiettivo)}. {formatNumber(objective.ferieRiportateAlProssimoAnno)} gg vengono riportati al {objective.anno + 1}.
                </p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function TechnicalDetail({ group, onEditPeriod, onDeletePeriod }) {
  const details = group.dettagliCalcolo || {};
  return (
    <div className="space-y-4 border-t border-stone-100 p-4 sm:p-5">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 xl:grid-cols-8">
        <Metric label="Minimo settimanale" value={group.necessari ?? '—'} />
        <Metric label="Cuochi in organico" value={formatNumber(group.organico)} tone="green" />
        <Metric label="Cuochi assenti" value={formatNumber(group.assentiOggi)} tone="stone" />
        <Metric label="Ferie maturate" value={`${formatNumber(group.maturato)} gg`} tone="navy" />
        <Metric label="Ferie godute" value={`${formatNumber(group.goduto)} gg`} tone="stone" />
        <Metric label="Ferie riportate" value={`${formatNumber(group.riportato)} gg`} tone="info" />
        <Metric label="Ferie da programmare" value={`${formatNumber(group.ferieDaCompletare)} gg`} tone="amber" />
        <Metric label="Ferie già programmate" value={`${formatNumber(group.programmato)} gg`} tone="stone" />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Metric label="Giornate-persona teoriche" value={formatNumber(group.giornatePersonaTeoriche)} tone="navy" />
        <Metric label="Copertura minima richiesta" value={formatNumber(group.giornatePersonaCoperturaMinima)} tone="amber" />
        <Metric label="Giornate-persona disponibili" value={formatNumber(group.giornatePersonaDisponibili)} tone="green" />
        <Metric label="Margine giornate-persona" value={formatNumber(group.margineGiornatePersona)} tone={group.margineGiornatePersona < 0 ? 'amber' : 'green'} />
      </div>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <Metric label="Ferie gestibili teoricamente" value={`${formatNumber(group.capacitaFerieTeorica)} gg`} tone="green" />
        <Metric label="Capacità reale calendario" value={`${formatNumber(group.capacitaFerieCalendario)} gg`} tone="navy" />
        <Metric label="Ferie residue teoriche" value={`${formatNumber(group.ferieResidueTeoriche)} gg`} tone="amber" />
        <Metric label="Ferie che richiedono copertura" value={`${formatNumber(group.ferieCheRichiedonoCopertura)} gg`} tone="amber" />
      </div>
      {group.stato === 'NON_DETERMINABILE' && (
        <div className="flex gap-2 rounded-xl border border-stone-200 bg-stone-50 p-3 text-xs text-stone-700">
          <Info className="h-4 w-4 shrink-0" />
          <div><strong>Da verificare.</strong><div className="mt-1">{(details.motivi || ['Mancano dati indispensabili.']).join(' ')}</div></div>
        </div>
      )}
      <section>
        <h3 className="text-sm font-semibold text-brand-navy">Come è stato calcolato</h3>
        <p className="mt-1 text-xs text-stone-500">Abbiamo verificato ogni giornata-persona rispetto alla copertura minima, tenuto conto delle assenze e distribuito le ferie residue fino alla data obiettivo. Il controllo copre dal {formatDate(details.dataInizioProiezione)} al {formatDate(details.dataFineProiezione)} e non modifica ferie o turni.</p>
        {group.periodiCritici?.length > 0 && <div className="mt-3 rounded-xl border border-orange-100 bg-orange-50/50 p-3"><PeriodSummary periods={group.periodiCritici} /></div>}
        {group.fabbisognoAggiuntivo > 0 && <div className="mt-3"><CoverageSummary group={group} /></div>}
        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <div className="rounded-xl border border-stone-100 p-3 text-xs">
            <div className="font-semibold text-stone-800">Ferie già presenti</div>
             {details.ferieFisse?.length ? details.ferieFisse.map((period) => (
               <div key={`${period.id}-${period.dal}`} className="mt-2 flex items-center justify-between gap-2 text-stone-600">
                 <span className="min-w-0 truncate">{formatDate(period.dal)} → {formatDate(period.al)}</span>
                 <span className="flex shrink-0 gap-1">
                   <button type="button" className="rounded-lg px-2 py-1 text-[11px] font-semibold text-brand-blue hover:bg-blue-50" onClick={() => onEditPeriod(period)}>Modifica</button>
                   <button type="button" className="rounded-lg px-2 py-1 text-[11px] font-semibold text-red-700 hover:bg-red-50" onClick={() => onDeletePeriod(period)}>Elimina</button>
                 </span>
               </div>
             )) : <div className="mt-2 text-stone-500">Nessuna nel periodo.</div>}
          </div>
          <div className="rounded-xl border border-stone-100 p-3 text-xs">
            <div className="font-semibold text-stone-800">Ferie da programmare</div>
            <div className="mt-2 text-stone-600">{formatNumber(group.ferieDaCompletare)} gg complessivi da programmare.</div>
          </div>
        </div>
      </section>
    </div>
  );
}

function PlanningCard({ group, onEditPeriod, onDeletePeriod }) {
  const [open, setOpen] = useState(false);
  const extra = Number(group.fabbisognoAggiuntivo || 0);
  const locationLabel = formatLocationLabel(group);
  return (
    <article className="overflow-hidden rounded-2xl border border-app-border bg-app-surface" data-testid={`pianificazione-group-${group.repartoId || 'senza-reparto'}`}>
      <div className="p-3.5 sm:p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-brand-navy">{locationLabel}</div>
            <h2 className="mt-0.5 text-xs font-semibold uppercase tracking-wider text-stone-500">{group.repartoNome}</h2>
          </div>
          <div className="flex shrink-0 flex-wrap justify-end gap-2">
            {extra > 0 && (
              <button type="button" className="inline-flex items-center gap-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1.5 text-xs font-semibold text-brand-blue hover:bg-blue-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40" onClick={() => setOpen(true)} data-testid={`ferie-pianificazione-find-coverage-${group.repartoId || 'senza-reparto'}`}>
                <Users className="h-3.5 w-3.5" aria-hidden="true" /> Trova copertura
              </button>
            )}
            <button type="button" className="flex items-center gap-1 text-xs font-semibold text-stone-500 hover:text-brand-navy" onClick={() => setOpen(!open)} aria-expanded={open} aria-label={`${open ? 'Nascondi' : 'Mostra'} dettagli ${group.repartoNome}`}>
              {open ? 'Nascondi' : 'Dettagli'} {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <div className="mt-3 grid grid-cols-2 gap-2 sm:max-w-lg">
           <Metric label="Cuochi necessari" value={group.necessari ?? '—'} />
          <Metric label="Cuochi in organico" value={formatNumber(group.organico)} tone="green" />
        </div>
        <div className="mt-2 flex items-center justify-between gap-3 rounded-xl bg-stone-50 px-3 py-2.5">
          <span className="text-xs font-semibold text-stone-600">Ferie residue</span>
           <span className="font-display text-lg font-semibold text-brand-navy">{formatNumber(group.ferieDaCompletare)} gg</span>
        </div>
      <div className="mt-2 grid grid-cols-2 gap-2">
        <Metric
          label="Ferie utilizzabili ora"
          value={`${formatNumber(group.ferieUtilizzabiliConOrganico)} gg`}
          tone="green"
        />
        <Metric
          label="Richiedono copertura"
          value={`${formatNumber(group.ferieCheRichiedonoCopertura)} gg`}
          tone="amber"
        />
      </div>
         <div className="mt-2 flex items-center justify-between gap-3 rounded-xl border border-stone-100 bg-stone-50/70 px-3 py-2.5" data-testid={`ferie-pianificazione-group-objective-${group.repartoId || 'senza-reparto'}`}>
            <span className="text-xs text-stone-500">Cuochi aggiuntivi necessari</span>
            <strong className="text-sm text-brand-navy">+{formatNumber(extra)}</strong>
         </div>
        <div className="mt-3"><StatusLine group={group} /></div>
      </div>
       {open && <TechnicalDetail group={group} onEditPeriod={onEditPeriod} onDeletePeriod={onDeletePeriod} />}
    </article>
  );
}

export default function FeriePianificazione() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [year, setYear] = useState(Number(searchParams.get('anno')) || new Date().getFullYear());
  const [filters, setFilters] = useState({ company: 'all', location: 'all', department: 'all' });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [addingPeriod, setAddingPeriod] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const query = new URLSearchParams({ anno: String(year) });
      if (filters.company !== 'all') query.set('azienda_id', filters.company);
      if (filters.location !== 'all') query.set('locale_id', filters.location);
      if (filters.department !== 'all') query.set('reparto_id', filters.department);
      setData(await apiFetch(`/api/ferie/pianificazione?${query}`));
    } catch (err) {
      setError(err.message || 'Impossibile caricare la pianificazione');
    } finally {
      setLoading(false);
    }
  }, [year, filters]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (loading || location.hash !== '#ferie-pianificazione-calendario') return;
    const target = document.getElementById('ferie-pianificazione-calendario');
    if (!target) return;
    window.requestAnimationFrame(() => target.scrollIntoView({ behavior: 'smooth', block: 'start' }));
  }, [loading, location.hash]);

  const availableLocations = useMemo(
    () => (data?.ferie?.filters?.locali || []).filter((item) => filters.company === 'all' || item.aziendaId === filters.company),
    [data, filters.company],
  );
  const availableDepartments = useMemo(
    () => (data?.ferie?.filters?.reparti || []).filter((item) => filters.location === 'all' || item.localeId === filters.location),
    [data, filters.location],
  );
  const selectedCompany = useMemo(
    () => (data?.ferie?.filters?.aziende || []).find((item) => String(item.id) === String(filters.company)),
    [data, filters.company],
  );
  const selectedLocation = useMemo(
    () => (data?.ferie?.filters?.locali || []).find((item) => String(item.id) === String(filters.location)),
    [data, filters.location],
  );
  const selectedDepartment = useMemo(
    () => (data?.ferie?.filters?.reparti || []).find((item) => String(item.id) === String(filters.department)),
    [data, filters.department],
  );
  const filterSummary = [
    selectedCompany?.nome || 'Tutte le aziende',
    selectedLocation?.nome || 'Tutti i locali',
    selectedDepartment?.nome || 'Tutti i reparti',
  ];
  const hasActiveFilters = Object.values(filters).some((value) => value !== 'all');
  const groups = data?.gruppi || [];
  const planningEmployees = data?.ferie?.filters?.dipendenti || [];
  const openCalendar = () => document.getElementById('ferie-pianificazione-calendario')?.scrollIntoView({ behavior: 'smooth', block: 'start' });

  const deletePeriod = async (period) => {
    if (!window.confirm('Eliminare questo periodo ferie?')) return;
    try {
      await apiFetch(`/api/ferie/analisi/periodi/${period.id}`, { method: 'DELETE' });
      toast.success('Periodo eliminato');
      await load();
    } catch (err) {
      toast.error(err.message || 'Eliminazione non riuscita');
    }
  };

  return (
     <main className="mx-auto w-full max-w-5xl space-y-4 px-4 pb-24 pt-5 sm:px-6 sm:pb-10 lg:px-8" data-testid="ferie-pianificazione">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <Link to={`/ferie?anno=${year}`} className="inline-flex items-center gap-1 text-xs font-semibold text-brand-blue hover:underline"><ArrowLeft className="h-3.5 w-3.5" /> Torna a Ferie</Link>
          <h1 className="mt-3 font-display text-2xl font-semibold tracking-tight text-brand-navy sm:text-3xl">Ferie e Copertura</h1>
          <p className="mt-1 max-w-2xl text-sm text-stone-500">Riusciamo a far finire tutte le ferie entro il 31 dicembre senza scoprire i locali?</p>
        </div>
         <div className="flex flex-wrap items-center gap-2">
           <Button type="button" onClick={() => setAddingPeriod(true)} disabled={!planningEmployees.length} className="rounded-full bg-brand-green text-white hover:bg-brand-green-hover">
             <Plus className="mr-1.5 h-4 w-4" /> Inserisci ferie
           </Button>
          <select aria-label="Anno pianificazione" value={year} onChange={(event) => { const next = Number(event.target.value); setYear(next); setSearchParams({ anno: String(next) }, { replace: true }); }} className="h-10 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm font-semibold text-brand-navy">{(data?.ferie?.filters?.anni || [year]).map((item) => <option key={item} value={item}>{item}</option>)}</select>
          <Button type="button" variant="outline" size="icon" onClick={load} disabled={loading} aria-label="Aggiorna pianificazione"><RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /></Button>
        </div>
      </header>

      <section className="rounded-2xl border border-app-border bg-app-surface" aria-label="Filtri pianificazione" data-testid="ferie-pianificazione-filters">
        <div className="flex items-center gap-2 px-3 py-2.5 sm:px-4">
          <button
            type="button"
            className="flex min-w-0 flex-1 items-center gap-2 rounded-lg text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
            aria-controls="ferie-pianificazione-filter-panel"
            data-testid="ferie-pianificazione-filter-toggle"
          >
            <Filter className="h-4 w-4 shrink-0 text-brand-blue" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-semibold leading-5 text-brand-navy">Filtri</span>
              <span className="block break-words text-xs leading-5 text-stone-500" data-testid="ferie-pianificazione-filter-summary" aria-live="polite">
                {filterSummary.join(' · ')}
              </span>
            </span>
            {filtersOpen ? <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-stone-400" aria-hidden="true" /> : <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-stone-400" aria-hidden="true" />}
          </button>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={() => setFilters({ company: 'all', location: 'all', department: 'all' })}
              className="shrink-0 rounded-lg px-2 py-1 text-xs font-semibold text-brand-blue hover:bg-blue-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-blue/40"
              data-testid="ferie-pianificazione-filter-reset"
            >
              Reset
            </button>
          )}
        </div>
        <div id="ferie-pianificazione-filter-panel" hidden={!filtersOpen} className="border-t border-app-border px-3 pb-3 pt-3 sm:px-4 sm:pb-4">
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
            <label className="grid gap-1 text-xs font-semibold text-stone-600">
              Azienda
              <select aria-label="Azienda" value={filters.company} onChange={(event) => setFilters({ company: event.target.value, location: 'all', department: 'all' })} className="h-10 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm font-normal text-brand-navy">
                <option value="all">Tutte le aziende</option>
                {(data?.ferie?.filters?.aziende || []).map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-semibold text-stone-600">
              Locale
              <select aria-label="Locale" value={filters.location} onChange={(event) => setFilters((current) => ({ ...current, location: event.target.value, department: 'all' }))} className="h-10 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm font-normal text-brand-navy">
                <option value="all">Tutti i locali</option>
                {availableLocations.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </label>
            <label className="grid gap-1 text-xs font-semibold text-stone-600">
              Reparto
              <select aria-label="Reparto" value={filters.department} onChange={(event) => setFilters((current) => ({ ...current, department: event.target.value }))} className="h-10 rounded-xl border border-app-border-strong bg-app-surface px-3 text-sm font-normal text-brand-navy">
                <option value="all">Tutti i reparti</option>
                {availableDepartments.map((item) => <option key={item.id} value={item.id}>{item.nome}</option>)}
              </select>
            </label>
          </div>
        </div>
      </section>

      {!loading && !error && data && <PlanningOverview data={data} year={year} groups={groups} onOpenCalendar={openCalendar} />}
      {!loading && !error && data && <GeneralSummary kpi={data.kpi} summary={data.riepilogoGenerale} objectives={data.obiettivi} />}
      {loading && <div className="flex items-center justify-center rounded-2xl border border-app-border bg-app-surface p-12 text-sm text-stone-500"><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Sto verificando il piano…</div>}
      {!loading && error && <div className="rounded-2xl border border-red-200 bg-red-50 p-5 text-sm text-red-800"><div className="flex items-center gap-2 font-semibold"><XCircle className="h-5 w-5" /> Pianificazione non disponibile</div><p className="mt-1">{error}</p></div>}
      {!loading && !error && groups.length === 0 && <div className="rounded-2xl border border-dashed border-stone-300 bg-stone-50 p-10 text-center text-sm text-stone-500">Nessun reparto nel perimetro autorizzato.</div>}
        {!loading && !error && data && <CalendarView groups={groups} year={year} />}
        {!loading && !error && groups.length > 0 && <section className="space-y-3"><div><h2 className="font-display text-lg font-semibold text-brand-navy">Dettaglio delle singole cucine</h2><p className="text-xs text-stone-500">Numeri e risultato di ogni cucina, senza elenchi di dipendenti.</p></div>{groups.map((group) => <PlanningCard key={`${group.gruppoId || group.localeId}-${group.repartoId || 'none'}`} group={group} onEditPeriod={setEditingPeriod} onDeletePeriod={deletePeriod} />)}</section>}
       {addingPeriod && <PeriodEditor employeeId={planningEmployees[0]?.id} year={year} employees={planningEmployees} allowEmployeeChange defaultState="programmata" onClose={() => setAddingPeriod(false)} onSaved={() => { setAddingPeriod(false); load(); }} />}
       {editingPeriod && <PeriodEditor period={editingPeriod} year={year} employees={planningEmployees} allowEmployeeChange onClose={() => setEditingPeriod(null)} onSaved={() => { setEditingPeriod(null); load(); }} />}
    </main>
  );
}